import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ConnectMetaBanner — the "Connect your Meta Business account" prompt banner.
 *
 * A full-width call-to-action shown above a dashboard/channels area when the org
 * still needs to authorize Meta before WhatsApp / Instagram / Facebook channels
 * can be added. It has two states:
 *
 *   • disconnected — the vibrant Meta-gradient hero with the Connect CTA.
 *   • connected    — a quiet green confirmation strip with an optional Disconnect.
 *
 * Meta branding and copy are baked in on purpose; this is the Meta banner, not a
 * generic connect-account banner. Wire `onConnect` / `onDisconnect` to your auth
 * flow; the component owns only presentation.
 */
export interface ConnectMetaBannerProps {
  /** Whether the Meta Business account is already authorized. */
  connected: boolean;
  /** Fired when the user clicks "Connect Meta account". */
  onConnect: () => void;
  /** Optional — when provided (and connected), renders a Disconnect link. */
  onDisconnect?: () => void;
  className?: string;
}

/**
 * The Meta glyph, rendered from the app's public SVG asset
 * (`/onboardingimg/meta.svg`) via a CSS mask. Masking means the icon inherits
 * the surrounding text color through `currentColor` — so it shows white on the
 * gradient banner chip and dark on the white button, with no per-surface color
 * variants. Size comes from the `className` (e.g. `h-6 w-6`).
 */
function MetaGlyph({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-block bg-current", className)}
      style={{
        WebkitMaskImage: "url(/onboardingimg/meta.svg)",
        maskImage: "url(/onboardingimg/meta.svg)",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        WebkitMaskSize: "contain",
        maskSize: "contain",
      }}
    />
  );
}

export function ConnectMetaBanner({
  connected,
  onConnect,
  onDisconnect,
  className,
}: ConnectMetaBannerProps) {
  if (connected) {
    return (
      <div
        className={cn(
          "flex items-center gap-3 border-b border-[#B7E9C6] bg-[#E7F7EC] px-6 py-2.5 lg:px-10",
          className,
        )}
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#0EB64F]">
          <Check className="h-4 w-4" strokeWidth={2.6} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-[#076A2D]">
            Meta Business connected
          </p>
          <p className="text-[12px] text-[#0A8A3B]/80">
            You can now add WhatsApp, Instagram or Facebook as separate channels.
          </p>
        </div>
        {onDisconnect && (
          <button
            type="button"
            onClick={onDisconnect}
            className="text-[12px] font-medium text-[#076A2D] underline decoration-[#B7E9C6] underline-offset-2 hover:text-[#054E20]"
          >
            Disconnect
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative overflow-hidden text-white", className)}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#0081FB] via-[#4F46E5] to-[#7C3AED]" />
      <div className="absolute -right-6 -top-8 h-40 w-40 rounded-full bg-white/15 blur-2xl" />
      <div className="absolute -bottom-8 -left-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

      <div className="relative flex flex-col items-start gap-4 px-6 py-[26px] sm:flex-row sm:items-center lg:px-10">
        <div className="flex items-center gap-3 sm:flex-1">
          <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/25 backdrop-blur">
            <MetaGlyph className="h-[30px] w-[30px]" />
          </div>
          <div className="min-w-0">
            <p className="text-[20px] font-semibold leading-tight">
              Connect your Meta Business account
            </p>
            <p className="mt-1 text-[16px] leading-relaxed text-white/85">
              Required to add WhatsApp, Instagram or Facebook as channels. You&apos;ll
              still link each channel separately once Meta is authorized.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:shrink-0">
          <button
            type="button"
            onClick={onConnect}
            className="flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-[16px] font-semibold text-[#101828] shadow-[0_2px_8px_rgba(0,0,0,0.15)] hover:bg-white/95"
          >
            <MetaGlyph className="h-4 w-4" />
            Connect Meta account
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConnectMetaBanner;
